import React from "react";
import {
  FiMapPin,
  FiMessageSquare,
  FiTrash2,
  FiEye,
  FiCalendar,
  FiUser,
  FiImage,
  FiBriefcase,
} from "react-icons/fi";
import { formatCurrency, formatDate, resolveAssetUrl, stripHtml } from "../utils/formatters";
import "./PostCard.css";

const PostCard = ({
  post,
  onViewDetails,
  onOpenComments,
  onDelete,
  canDelete = false,
}) => {
  const {
    service,
    description,
    budget,
    city_name,
    state_name,
    area_name,
    property_type,
    customer,
    partner,
    user,
    created_on,
    created_at,
  } = post || {};

  const imagesList = Array.isArray(post?.images) ? post.images : [];
  const firstImage = imagesList.length > 0 ? imagesList[0] : null;
  const imageUrl = firstImage?.image_url || firstImage?.imagePath || firstImage?.url || "";
  const resolvedImg = imageUrl ? resolveAssetUrl(imageUrl) : null;

  const propertyTypeName =
    typeof property_type === "object" ? property_type?.name : property_type;

  const creatorName =
    customer?.first_name || partner?.first_name || user?.first_name || post?.customer_name
      ? `${customer?.first_name || partner?.first_name || user?.first_name || post?.customer_name || ""} ${
          customer?.last_name || partner?.last_name || user?.last_name || ""
        }`.trim()
      : "User";

  const descriptionSnippet = stripHtml(description);
  const formattedBudget = formatCurrency(budget);
  const postDate = created_on || created_at;

  return (
    <div className="shared-post-card">
      {/* Thumbnail area */}
      <div
        className="shared-post-card__media"
        onClick={() => onViewDetails(post)}
      >
        {resolvedImg ? (
          <img
            src={resolvedImg}
            alt={service || "Post Image"}
            className="shared-post-card__img"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.classList.add("shared-post-card__media--fallback");
            }}
          />
        ) : (
          <div className="shared-post-card__placeholder">
            <FiImage size={36} />
            <span>Requirement Post</span>
          </div>
        )}

        <div className="shared-post-card__badges">
          {service && (
            <span className="shared-post-card__service-badge">
              <FiBriefcase size={12} />
              <span>{service}</span>
            </span>
          )}
          {propertyTypeName && (
            <span className="shared-post-card__type-badge">
              {propertyTypeName}
            </span>
          )}
        </div>

        {formattedBudget && (
          <div className="shared-post-card__budget-badge">
            <span className="shared-post-card__budget-label">Budget</span>
            <span className="shared-post-card__budget-val">{formattedBudget}</span>
          </div>
        )}
      </div>

      {/* Content area */}
      <div className="shared-post-card__body">
        <h3
          className="shared-post-card__title"
          onClick={() => onViewDetails(post)}
          title={service || "Customer Requirement"}
        >
          {service ? `${service} Requirement` : "Requirement Post"}
        </h3>

        {/* Location info */}
        <div className="shared-post-card__location">
          <FiMapPin className="shared-post-card__loc-icon" />
          <span>
            {[area_name, city_name, state_name].filter(Boolean).join(", ") ||
              "Location Not Specified"}
          </span>
        </div>

        {/* Description snippet */}
        {descriptionSnippet && (
          <p className="shared-post-card__description">
            {descriptionSnippet.length > 120
              ? `${descriptionSnippet.slice(0, 120)}...`
              : descriptionSnippet}
          </p>
        )}

        {/* Creator / date metadata */}
        <div className="shared-post-card__meta">
          <div className="shared-post-card__author" title={`Posted by ${creatorName}`}>
            <FiUser className="shared-post-card__meta-icon" />
            <span className="shared-post-card__author-name">{creatorName}</span>
          </div>
          {postDate && (
            <div className="shared-post-card__date" title={`Posted on ${formatDate(postDate)}`}>
              <FiCalendar className="shared-post-card__meta-icon" />
              <span>{formatDate(postDate)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action footer */}
      <div className="shared-post-card__footer">
        <div className="shared-post-card__footer-left">
          <button
            type="button"
            className="shared-post-card__btn shared-post-card__btn--primary"
            onClick={() => onViewDetails(post)}
          >
            <FiEye size={14} />
            <span>Details</span>
          </button>

          <button
            type="button"
            className="shared-post-card__btn shared-post-card__btn--secondary"
            onClick={() => onOpenComments(post)}
            title="Discussion & Inquiries"
          >
            <FiMessageSquare size={14} />
            <span>Discussion</span>
          </button>
        </div>

        {canDelete && (
          <div className="shared-post-card__footer-right">
            <button
              type="button"
              className="shared-post-card__btn shared-post-card__btn--danger"
              onClick={() => onDelete(post)}
              title="Delete Post"
            >
              <FiTrash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostCard;
